import { AfterViewChecked, AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FullBleedImagesInfo } from '@suiteux/suiteux-shell';
import { merge, Observable, Subscription } from 'rxjs';
import { debounceTime, filter, take } from 'rxjs/operators';

import { AppGalleryConfigService } from '../app-gallery/app-gallery-config.service';
import { AppTilesConfigService } from '../app-tiles/shared/app-tiles-config.service';
import { InstallConfigService } from '../install/install-config.service';
import { SearchConfigService } from '../search-results/services/search-config.service';
import { FabricConstants } from '../shared/constants/fabric.constant';
import { OfficeRoute } from '../shared/constants/office-route.enum';
import { KnownAreas } from '../shared/instrumentation/instrumentation.service';
import { FileType } from '../shared/models/file-item.model';
import { ObservableScope } from '../shared/models/observable-scope.enum';
import { Workload } from '../shared/models/workload.model';
import { ConsumerSkuType } from '../shared/services/consumer-license.service';
import { DeviceDetector } from '../shared/services/device-detector.service';
import { AppEvent, EventService } from '../shared/services/event.service';
import { OfficeRouteItem, OfficeRouteService, RouteChangeEvent } from '../shared/services/office-route.service';
import { FullBleedImageType, ThemingService } from '../shared/services/theming.service';
import { WindowService } from '../shared/services/window.service';
import { WorkloadsService } from '../shared/services/workloads.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ConsumerInstallConfigService } from '../shared/shared-context/consumer-install-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { LocService } from '../shared/shared-context/loc.service';
import { WacStartConfigService } from '../wac-start/wac-start-config.service';
import { getAnnouncementMarkup } from '../wac-start/wac-start.utility';
import { WelcomeContentService } from './welcome-content.service';

import './hero.component.css';

@Component({
    selector: 'ohp-hero',
    templateUrl: './hero.component.html'
})
export class HeroComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {

    @ViewChild('heroAnnouncement') public heroAnnouncement: ElementRef;

    public showAppTilesHeroTitle: boolean;
    public showCreateButton: boolean;
    public appsInHero: boolean;
    public hasSideImages: boolean;
    public isPatternImage: boolean;
    public showApps: boolean;
    public showInstall: boolean;
    public showInstallButton: boolean;
    public heroTitle: string;
    public backgroundStyle: { [key: string]: string } = {};
    public bleedBackgroundStyle: { [key: string]: string } = {};
    public heroImageStyle: { [key: string]: string } = {};
    public heroLeftImageStyle: { [key: string]: string } = {};
    public heroRightImageStyle: { [key: string]: string } = {};
    public heroTitleGradient: { [key: string]: string } = {};
    public activeShades: Array<string>;
    public activeThemeId: string;
    public onAppGalleryPage: boolean;
    public onCreatePage: boolean;
    public onTemplatesPage: boolean;
    public onSearchPage: boolean;
    public store: Workload;
    public accessibilityTitle: string;
    public backButtonId: string;
    public backButtonAriaLabel: string;
    public showBackButton: boolean;
    public defaultBackRoute: OfficeRouteItem;
    public showFASTBreadcrumb: boolean;
    public appContext: FileType;
    public showCreateLocation: boolean;
    public fullBleedThemingEnabled: boolean;
    public fullBleedFontColor: string;
    public fullBleedBackplateEnabled: boolean;
    public fullBleedBackplateStyle: { [key: string]: string } = {};
    public isConsumer: boolean;
    public isMobile: boolean;
    public bottomTrayEnabled: boolean;
    public componentArea: KnownAreas;
    public showHeroButtons: boolean;
    public hideConsumerBackgroundImage: boolean;
    public onHomePageOrPwa: boolean;
    public appBarEnabled: boolean;
    public myAccountLinkText: string;
    public myAccountUrl: string;
    public isPaidConsumer: boolean;
    public leftNavReact: boolean;
    public isContextualRailEnabled: boolean;
    public isSearchSiteScoped: boolean = false;
    public fluentRefresh: boolean;
    public suggestedActions: boolean;

    private fullBleedColor: string;
    private fullBleedImages: FullBleedImagesInfo;
    private pwaEnabled: boolean;
    private searchEnabled: boolean;
    private currentRoute: OfficeRoute;
    private greetingSubscription: Subscription;
    private routeSubscription: Subscription;
    private changeTitleSubscription: Subscription;
    private resizeSubscription: Subscription;
    private fullBleedSubscription: Subscription;
    private announcementListenerAdded: boolean;
    public exitSearchInSearchBoxSubscription: Subscription;
    public announcementMarkup : string;
    public quickAccessEnabled: boolean;
    public noGutter: boolean;

    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly appGalleryConfigService: AppGalleryConfigService,
        private readonly appTilesConfigService: AppTilesConfigService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly consumerInstallConfigService: ConsumerInstallConfigService,
        private readonly contextService: ContextService,
        private readonly deviceDetector: DeviceDetector,
        private readonly eventService: EventService,
        private readonly installConfigService: InstallConfigService,
        private readonly locService: LocService,
        private readonly officeRouteService: OfficeRouteService,
        private readonly searchConfigService: SearchConfigService,
        private readonly themingService: ThemingService,
        private readonly welcomeContentService: WelcomeContentService,
        private readonly windowService: WindowService,
        private readonly workloadsService: WorkloadsService,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly wacStartConfigService: WacStartConfigService
    ) {
    }

    public ngOnInit() {
        const capabilities = this.capabilitiesService.capabilities;
        const appGalleryEnabled = !!this.appGalleryConfigService.config;
        const wacStartEnabled = capabilities.wacStartPageEnabled;
        this.appsInHero = capabilities.appsInHero;
        this.bottomTrayEnabled = capabilities.bottomTrayEnabled;
        this.searchEnabled = capabilities.showSearch && !!this.searchConfigService.config;
        this.pwaEnabled = capabilities.pwaEnabled;
        this.isConsumer = this.startupConfigService.config.authType === AuthType.MSA;
        this.isMobile = this.deviceDetector.isMobileDevice();
        this.hideConsumerBackgroundImage = this.isConsumer && !this.onHomePageOrPwa;
        this.showAppTilesHeroTitle = this.appsInHero && !this.bottomTrayEnabled;
        this.myAccountLinkText = this.appTilesConfigService.config.resources.MyAccountConsumerLinkText;
        this.myAccountUrl = this.appTilesConfigService.config.myAccountUrl;
        this.leftNavReact = capabilities.leftNavReact;
        this.quickAccessEnabled = capabilities.quickAccessV1Enabled;
        this.fluentRefresh = capabilities.fluentRefresh;
        this.appBarEnabled = capabilities.appBarEnabled;
        this.announcementListenerAdded = false;
        this.suggestedActions = capabilities.suggestedActionsEnabled;

        if (this.isConsumer) {
            this.consumerInstallConfigService.getConfig().then(consumerInstallConfig => {
                const subscriptionStatus = consumerInstallConfig && consumerInstallConfig.currentSkuInfo ? consumerInstallConfig.currentSkuInfo.skuType : ConsumerSkuType.NotDetected;
                this.isPaidConsumer = !(subscriptionStatus === ConsumerSkuType.NotSubscribed || subscriptionStatus === ConsumerSkuType.NonActive || subscriptionStatus === ConsumerSkuType.NotDetected);
                if (subscriptionStatus === ConsumerSkuType.NotSubscribed || subscriptionStatus === ConsumerSkuType.NonActive) {
                    if (this.installConfigService.config.dimeEnabled) {
                        this.eventService.events.withScope(ObservableScope.OnlyFuture)
                            .pipe(filter(event => event.name === AngularEvents.convertedToPaid), take(1))
                            .toPromise()
                            .then(() => { this.isPaidConsumer = true; });
                    }
                }
            });
        } else {
            this.isPaidConsumer = false;
        }

        this.greetingSubscription = this.welcomeContentService.greetingObservable.subscribe(homeGreeting => {
            // Update existing title if greeting changes while user is on Home page
            if (this.heroTitle && (!this.currentRoute || this.currentRoute === OfficeRoute.OfficeHome || this.currentRoute === OfficeRoute.Pwa || (this.currentRoute === OfficeRoute.AppGallery && this.isConsumer))) {
                this.heroTitle = homeGreeting;
            }
        });

        if (this.searchEnabled || appGalleryEnabled || wacStartEnabled) {
            let routerObservable: Observable<RouteChangeEvent>;

            if (!this.officeRouteService.hasNavigated()) {
                routerObservable = merge(
                    this.officeRouteService.getRouteChangeStartObservable().pipe(take(1)),
                    this.officeRouteService.getRouteChangeEndObservable()
                );
            } else {
                routerObservable = this.officeRouteService.getRouteChangeEndObservable();
            }

            this.routeSubscription = routerObservable.subscribe(routeEvent => {
                this.setHeroFlags(routeEvent);
            });

            this.changeTitleSubscription = this.eventService
                .events.withScope(ObservableScope.OnlyFuture)
                .pipe(filter((event: AppEvent) => event.name === AngularEvents.changeTitle))
                .subscribe(event => {
                    if (!event || !event.args || !event.args.length) {
                        return;
                    }
                    const args = event.args;
                    this.heroTitle = (this.isConsumer && this.onAppGalleryPage) ? this.welcomeContentService.currentGreeting : args[0];
                    this.accessibilityTitle = args.length > 1 ? args[1] : this.heroTitle;
                });
        } else {
            this.showApps = this.appsInHero && !this.leftNavReact;
            this.showInstall = true;
            this.showInstallButton = true;
            this.heroTitle = this.welcomeContentService.currentGreeting;
            this.accessibilityTitle = this.locService.AuthPageTitle;
        }

        this.fullBleedSubscription = this.themingService.fullBleedTheming.subscribe(event => {
            const { shouldFullBleed, bleedColor, themeData, bleedFontColor } = event;

            this.fullBleedThemingEnabled = shouldFullBleed;
            this.fullBleedColor = bleedColor;
            this.fullBleedFontColor = this.fullBleedThemingEnabled ? bleedFontColor : '';
            this.activeThemeId = themeData.theme.themeId;
            const reactTheme = themeData.theme.reactShellTheme;
            this.fullBleedImages = reactTheme ? reactTheme.FullBleedImages : null;
            this.setResizeSubscription();
            this.setFullBleedBackground(this.fullBleedThemingEnabled, this.fullBleedImages, this.fullBleedColor, this.windowService.innerWidth);
        });

    }

    public ngAfterViewInit() {
        const sharedContext = this.contextService.sharedContext;
        if (!this.appsInHero) {
            if (sharedContext.heroImageUrl) {
                this.backgroundStyle['background-image'] = `url(${sharedContext.heroImageUrl})`;
            }

            this.heroTitleGradient['background-image'] = `url(${sharedContext.heroTitleGradientImageUrl})`;
        }
        this.setResizeSubscription();
    }

    public ngAfterViewChecked() {
        if (this.announcementListenerAdded) {
            return;
        }

        const newStreamElement = this.heroAnnouncement?.nativeElement?.getElementsByClassName('new-stream-announcement');
        const classicStreamElement = this.heroAnnouncement?.nativeElement?.getElementsByClassName('classic-stream-announcement');

        if (newStreamElement && newStreamElement[0]) {
            newStreamElement[0].addEventListener('focus', () => {
                newStreamElement[0].scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'end'});
            });
        }

        if (classicStreamElement && classicStreamElement[0]) {
            classicStreamElement[0].addEventListener('focus', () => {
                classicStreamElement[0].scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'end'});
            });
            this.announcementListenerAdded = true;
        }
    }

    public ngOnDestroy() {
        if (this.greetingSubscription) {
            this.greetingSubscription.unsubscribe();
        }

        if (this.routeSubscription) {
            this.routeSubscription.unsubscribe();
        }

        if (this.changeTitleSubscription) {
            this.changeTitleSubscription.unsubscribe();
        }

        if (this.resizeSubscription) {
            this.resizeSubscription.unsubscribe();
        }

        if (this.fullBleedSubscription) {
            this.fullBleedSubscription.unsubscribe();
        }
    }

    public backToSearchPage() {
        this.officeRouteService.back({ route: OfficeRoute.Search });
    }

    private setResizeSubscription() {
        if (!this.resizeSubscription) {
            this.resizeSubscription = this.eventService.events.withScope(ObservableScope.OnlyFuture).pipe(
                filter(event => event.name === AngularEvents.contentWidthChanged || event.name === AngularEvents.windowResized),
                debounceTime(10)
            ).subscribe(_ => {
                this.setFullBleedBackground(this.fullBleedThemingEnabled, this.fullBleedImages, this.fullBleedColor, this.windowService.innerWidth);
            });
        }
    }

    private setHeroFlags(routeEvent: RouteChangeEvent) {
        const currentRouteItem = routeEvent.currentRoute;
        this.currentRoute = currentRouteItem ? currentRouteItem.route : null;
        this.onCreatePage = this.currentRoute === OfficeRoute.Create;
        this.onSearchPage = this.currentRoute === OfficeRoute.Search || this.currentRoute === OfficeRoute.SearchSiteScoped || this.currentRoute === OfficeRoute.SearchPersonScoped;
        this.onAppGalleryPage = this.currentRoute === OfficeRoute.AppGallery;
        this.onHomePageOrPwa = !this.currentRoute || this.currentRoute === OfficeRoute.OfficeHome || this.currentRoute === OfficeRoute.Pwa;
        this.showApps = this.onHomePageOrPwa && this.appsInHero && !this.leftNavReact;
        this.showFASTBreadcrumb = this.currentRoute === OfficeRoute.SearchSiteScoped;
        this.appContext = currentRouteItem ? currentRouteItem.app : null;
        this.hideConsumerBackgroundImage = this.isConsumer && !this.onHomePageOrPwa;
        this.announcementMarkup = getAnnouncementMarkup(this.locService, this.wacStartConfigService, this.appContext);

        if (this.onAppGalleryPage) {
            this.workloadsService.getWorkloads().then((workloads: Array<Workload>) => { this.store = workloads.find(_ => _.Id === 'ShellOfficeStore'); });
        }

        this.showInstall = this.onHomePageOrPwa || this.onAppGalleryPage;
        this.showInstallButton = this.capabilitiesService.capabilities.isPwaInUwp && this.installConfigService.config.pwaInstallMoveEnabled ? this.onAppGalleryPage : this.onHomePageOrPwa || this.onAppGalleryPage;
        this.showHeroButtons = this.onAppGalleryPage || this.onHomePageOrPwa;

        if (!this.onSearchPage && this.activatedRoute.snapshot.queryParams['q'] && !this.isConsumer) {
            // Add backwards compatibility for 'q' parameter without '/search' url.
            this.officeRouteService.navigateTo({ route: OfficeRoute.Search }, true);
            return;
        }
        const exitSearchEvent = this.eventService.whenEventFired(AngularEvents.exitSearchInSearchBox, ObservableScope.PastAndFuture);
        const contextualRailEvent = this.eventService.whenEventFired(AngularEvents.isContextualRailEnabled, ObservableScope.PastAndFuture);

        this.backButtonId = 'back-button';
        this.backButtonAriaLabel = '';
        let showBack: Promise<boolean> = Promise.resolve(false);
        this.defaultBackRoute = null;
        this.onTemplatesPage = false;
        switch (this.currentRoute) {
            case OfficeRoute.Search:
            case OfficeRoute.SearchPersonScoped:
                if (this.isConsumer) {
                    this.accessibilityTitle = this.locService.SearchResults;
                }
                this.backButtonId = 'search-close-button';
                this.backButtonAriaLabel = this.locService.CloseSearchResultsAriaLabel;
                this.defaultBackRoute = { route: this.pwaEnabled ? OfficeRoute.Pwa : OfficeRoute.OfficeHome };
                this.componentArea = KnownAreas.SearchResults;
                showBack = exitSearchEvent.then(event => !event.args[0]);
                break;
            case OfficeRoute.SearchSiteScoped:
                this.backButtonId = 'search-site-scoped-close-button';
                this.backButtonAriaLabel = this.locService.CloseSearchResultsAriaLabel;
                this.defaultBackRoute = { route: OfficeRoute.Search };
                showBack = exitSearchEvent.then(event => !event.args[0]);
                break;
            case OfficeRoute.AppGallery:
                this.accessibilityTitle = this.locService.AppGalleryHeroTitle;
                this.backButtonId = 'app-gallery-close-button';
                this.componentArea = KnownAreas.AppGalleryHero;
                break;
            case OfficeRoute.Templates:
                // Templates Page title will be set via titleChange event listener
                showBack = Promise.resolve(true);
                this.defaultBackRoute = { route: OfficeRoute.Start, app: currentRouteItem.app };
                this.onTemplatesPage = true;
                this.componentArea = KnownAreas.Templates;
                break;
            case OfficeRoute.MyForms:
            case OfficeRoute.GroupForms:
                showBack = Promise.resolve(true);
                this.defaultBackRoute = { route: OfficeRoute.Start, app: currentRouteItem.app };
                break;
            case OfficeRoute.Start:
                // Start Page title will be set via titleChange event listener
                break;
            default:
                this.componentArea = KnownAreas.Hero;
        }
        this.isSearchSiteScoped = this.currentRoute === OfficeRoute.SearchSiteScoped;
        this.setShowBack(showBack);
        this.setContextualRail(contextualRailEvent.then(event => event.args[0]));
        this.heroTitle = this.onHomePageOrPwa ? this.welcomeContentService.currentGreeting : this.accessibilityTitle;
        this.showCreateLocation = this.onTemplatesPage && this.appContext !== FileType.Sway && this.appContext !== FileType.Forms;
        this.noGutter = this.fluentRefresh || (this.quickAccessEnabled && this.currentRoute === OfficeRoute.OfficeHome && !this.isConsumer);
    }

    private async setShowBack(show: Promise<boolean>) {
        this.showBackButton = await show;
        this.showAppTilesHeroTitle = !this.bottomTrayEnabled && (this.showBackButton || this.showFASTBreadcrumb || (this.onHomePageOrPwa && this.appsInHero));
    }

    private async setContextualRail(isContextualRailEnabled: Promise<boolean>) {
        this.isContextualRailEnabled = await isContextualRailEnabled;
    }

    private setFullBleedBackground(fullBleedThemingEnabled: boolean, fullBleedImages: FullBleedImagesInfo, bleedColor: string, width: number): void {
        // Needed to make current design accessible on mobile
        // TODO: Remove when mobile designs or explore all your apps is updated
        const backplateOverride = ThemingService.eduThemeBackplateOverrides[this.activeThemeId];
        this.fullBleedBackplateEnabled = backplateOverride && width < FabricConstants.MediumScreenMaxWidth && this.fullBleedThemingEnabled;
        this.fullBleedBackplateStyle = this.fullBleedBackplateEnabled ? Object.assign({ 'color': this.fullBleedFontColor }, backplateOverride) : { 'color': this.fullBleedFontColor };

        this.bleedBackgroundStyle = fullBleedThemingEnabled ? { 'background-color': bleedColor } : {};

        const type = fullBleedThemingEnabled && fullBleedImages ? fullBleedImages.Type : null;
        this.isPatternImage = type === FullBleedImageType.Pattern;
        this.hasSideImages = type === FullBleedImageType.Side;

        this.heroLeftImageStyle = this.getBackgroundImageStyle(this.hasSideImages && fullBleedImages ? fullBleedImages.Side : null);
        this.heroRightImageStyle = this.getBackgroundImageStyle(this.hasSideImages && fullBleedImages ? fullBleedImages.Side : null);
        this.heroImageStyle = this.getBackgroundImageStyle(
            fullBleedThemingEnabled && fullBleedImages ? this.themingService.getEduHeaderImageForWidth(fullBleedImages, width)
                : null
        );
    }

    private getBackgroundImageStyle(url: string) {
        return url && url.length ? { 'background-image': `url(${url})` } : {};
    }
}
